/** Extracts Get Insights' analysis content from the selected shape: table
 *  cells via the PowerPoint JS API, image/chart data by parsing the raw
 *  .pptx (Office.js exposes neither directly). */
import { strFromU8, unzipSync, type Unzipped } from "fflate";
import { getActivePresentationBytes } from "../../ColorTools/services/pptxBytes";
import { SLIDE_WIDTH, SLIDE_HEIGHT } from "../../../const";
import type { InsightsChartSeries, InsightsContent, InsightsSelectionContext } from "../../../types";

/* ── OOXML namespaces (kept local to this feature, matching colourOps.ts) ── */
const NS_P = "http://schemas.openxmlformats.org/presentationml/2006/main";
const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";
const NS_R = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const NS_C = "http://schemas.openxmlformats.org/drawingml/2006/chart";

const EXT_MIME: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".bmp": "image/bmp",
  ".tiff": "image/tiff",
  ".emf": "image/x-emf",
  ".wmf": "image/x-wmf",
  ".svg": "image/svg+xml",
};

// The backend's vision model only accepts raster formats — PowerPoint often
// embeds EMF/WMF/TIFF/SVG/BMP for charts, SmartArt, and vector clip-art.
const SUPPORTED_VISION_MIMES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

function u8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

/* ── Locate the selected slide's XML + path inside the .pptx zip ────────── */

function resolveSlidePath(zip: Unzipped, slideIndex: number): string {
  const presFile = zip["ppt/presentation.xml"];
  if (!presFile) {
    throw new Error("Couldn't open the presentation. Please save the file (Ctrl+S) and try again.");
  }
  const presDoc = new DOMParser().parseFromString(strFromU8(presFile), "text/xml");
  const sldIds = presDoc.getElementsByTagNameNS(NS_P, "sldId");
  if (slideIndex < 1 || slideIndex > sldIds.length) {
    throw new Error("Couldn't find the selected slide. Please try again.");
  }

  const target = sldIds[slideIndex - 1]!;
  const rId = target.getAttributeNS(NS_R, "id") ?? target.getAttribute("r:id");
  if (!rId) {
    throw new Error("Couldn't read the selected slide. Please try again.");
  }

  const relsFile = zip["ppt/_rels/presentation.xml.rels"];
  if (!relsFile) {
    throw new Error("Couldn't read the selected slide. Please try again.");
  }
  const relsDoc = new DOMParser().parseFromString(strFromU8(relsFile), "text/xml");
  for (const rel of relsDoc.getElementsByTagName("Relationship")) {
    if (rel.getAttribute("Id") === rId) {
      const relTarget = rel.getAttribute("Target");
      if (relTarget) return "ppt/" + relTarget;
    }
  }
  throw new Error("Couldn't read the selected slide. Please try again.");
}

function readSlideXml(zip: Unzipped, slidePath: string): string {
  const slideFile = zip[slidePath];
  if (!slideFile) {
    throw new Error("Couldn't read the selected slide. Please try again.");
  }
  return strFromU8(slideFile);
}

function slideRelsPath(slidePath: string): string {
  const slideFileName = slidePath.split("/").pop() ?? slidePath;
  return slidePath.replace(slideFileName, `_rels/${slideFileName}.rels`);
}

// Resolves a rels Target path relative to the slide it belongs to, e.g.
// "../media/image1.png" (from ppt/slides/_rels/slideN.xml.rels) → "ppt/media/image1.png".
function resolveRelativeTarget(slidePath: string, target: string): string {
  if (target.startsWith("../")) {
    const slideDir = slidePath.substring(0, slidePath.lastIndexOf("/"));
    const parentDir = slideDir.substring(0, slideDir.lastIndexOf("/"));
    return parentDir + "/" + target.substring(3);
  }
  if (!target.startsWith("ppt/")) {
    const slideDir = slidePath.substring(0, slidePath.lastIndexOf("/"));
    return slideDir + "/" + target;
  }
  return target;
}

function resolveRelTarget(zip: Unzipped, slidePath: string, rId: string): string | null {
  const relsFile = zip[slideRelsPath(slidePath)];
  if (!relsFile) return null;
  const relsDoc = new DOMParser().parseFromString(strFromU8(relsFile), "text/xml");
  for (const rel of relsDoc.getElementsByTagName("Relationship")) {
    if (rel.getAttribute("Id") === rId) {
      const target = rel.getAttribute("Target");
      return target ? resolveRelativeTarget(slidePath, target) : null;
    }
  }
  return null;
}

/* ── Image extraction ────────────────────────────────────────────────────── */

/** Finds the r:embed relationship id of the `<p:pic>` on the slide whose name matches.
 * Exported for selectionDetector.ts's ambiguous-placeholder probe. */
export function findImageREmbed(slideDoc: Document, shapeName: string): string | null {
  for (const pic of slideDoc.getElementsByTagNameNS(NS_P, "pic")) {
    const cNvPr = pic.getElementsByTagNameNS(NS_P, "nvPicPr")[0]?.getElementsByTagNameNS(NS_P, "cNvPr")[0];
    if ((cNvPr?.getAttribute("name") ?? "") !== shapeName) continue;
    const blip = pic.getElementsByTagNameNS(NS_P, "blipFill")[0]?.getElementsByTagNameNS(NS_A, "blip")[0];
    const rId = blip?.getAttributeNS(NS_R, "embed") || blip?.getAttribute("r:embed");
    if (rId) return rId;
  }
  return null;
}

function extractImageContent(
  zip: Unzipped,
  slideDoc: Document,
  slidePath: string,
  shapeName: string,
): Promise<InsightsContent> {
  const rId = findImageREmbed(slideDoc, shapeName);
  if (!rId) {
    throw new Error(`Couldn't find the image data for "${shapeName}" on this slide.`);
  }
  const imagePath = resolveRelTarget(zip, slidePath, rId);
  if (!imagePath) {
    throw new Error(`Couldn't resolve the image reference for "${shapeName}".`);
  }
  const imageFile = zip[imagePath];
  if (!imageFile) {
    throw new Error(`Image file not found in the presentation: ${imagePath}`);
  }

  const ext = imagePath.substring(imagePath.lastIndexOf(".")).toLowerCase();
  const mimeType = EXT_MIME[ext] ?? "image/png";
  if (!SUPPORTED_VISION_MIMES.has(mimeType)) {
    const extLabel = ext.replace(".", "").toUpperCase() || "unknown";
    throw new Error(
      `This image uses a ${extLabel} format that isn't supported for analysis. ` +
      "Common cases: PowerPoint embeds EMF/WMF (Windows metafiles) for charts, SmartArt, and vector " +
      "clip-art. Try replacing the image with a PNG/JPEG, or export the slide as an image and re-insert it.",
    );
  }

  return Promise.resolve({ type: "image", base64: u8ToBase64(imageFile), mimeType });
}

/* ── Chart extraction ────────────────────────────────────────────────────── */

/** Finds the r:id relationship id of the chart part backing the `<p:graphicFrame>` whose name
 * matches. Exported for selectionDetector.ts's ambiguous-placeholder probe. */
export function findChartRId(slideDoc: Document, shapeName: string): string | null {
  for (const graphicFrame of slideDoc.getElementsByTagNameNS(NS_P, "graphicFrame")) {
    const cNvPr = graphicFrame
      .getElementsByTagNameNS(NS_P, "nvGraphicFramePr")[0]
      ?.getElementsByTagNameNS(NS_P, "cNvPr")[0];
    if ((cNvPr?.getAttribute("name") ?? "") !== shapeName) continue;
    const chartEl = graphicFrame.getElementsByTagNameNS(NS_C, "chart")[0];
    const rId = chartEl?.getAttributeNS(NS_R, "id") || chartEl?.getAttribute("r:id");
    if (rId) return rId;
  }
  return null;
}

function firstV(container: Element | undefined): string {
  const v = container?.getElementsByTagNameNS(NS_C, "v")[0];
  return v?.textContent ?? "";
}

function allV(container: Element | undefined): string[] {
  if (!container) return [];
  return Array.from(container.getElementsByTagNameNS(NS_C, "v")).map((v) => v.textContent ?? "");
}

function extractChartTitle(doc: Document): string | null {
  const titleEl = doc.getElementsByTagNameNS(NS_C, "title")[0];
  if (!titleEl) return null;
  const richText = Array.from(titleEl.getElementsByTagNameNS(NS_A, "t"))
    .map((t) => t.textContent ?? "")
    .join("");
  if (richText.trim()) return richText.trim();
  const refText = firstV(titleEl);
  return refText.trim() || null;
}

function extractSeries(serEl: Element): InsightsChartSeries {
  const txEl = serEl.getElementsByTagNameNS(NS_C, "tx")[0];
  const valEl = serEl.getElementsByTagNameNS(NS_C, "val")[0];
  return {
    name: firstV(txEl) || "Series",
    values: allV(valEl),
  };
}

function parseChartXml(chartXml: string): InsightsContent {
  const doc = new DOMParser().parseFromString(chartXml, "text/xml");
  const serEls = Array.from(doc.getElementsByTagNameNS(NS_C, "ser"));
  const categories = allV(serEls[0]?.getElementsByTagNameNS(NS_C, "cat")[0]);
  return {
    type: "chart",
    title: extractChartTitle(doc),
    categories,
    series: serEls.map(extractSeries),
  };
}

function extractChartContent(
  zip: Unzipped,
  slideDoc: Document,
  slidePath: string,
  shapeName: string,
): Promise<InsightsContent> {
  const rId = findChartRId(slideDoc, shapeName);
  if (!rId) {
    throw new Error(`Couldn't find the chart data for "${shapeName}" on this slide.`);
  }
  const chartPath = resolveRelTarget(zip, slidePath, rId);
  const chartFile = chartPath ? zip[chartPath] : undefined;
  if (!chartFile) {
    throw new Error(`Chart file not found in the presentation for "${shapeName}".`);
  }
  return Promise.resolve(parseChartXml(strFromU8(chartFile)));
}

/* ── Table extraction (PowerPoint JS API — no zip parsing needed) ───────── */

async function findShapeOnSlide(
  context: PowerPoint.RequestContext,
  slideIndex: number,
  shapeId: string,
): Promise<PowerPoint.Shape | null> {
  const slides = context.presentation.slides;
  slides.load("items");
  await context.sync();

  const slide = slides.items[slideIndex - 1];
  if (!slide) return null;

  slide.shapes.load("items");
  await context.sync();

  for (const shape of slide.shapes.items) {
    shape.load("id");
  }
  await context.sync();

  return slide.shapes.items.find((s) => s.id === shapeId) ?? null;
}

function extractTableContent(selection: InsightsSelectionContext): Promise<InsightsContent> {
  if (!hasPpt()) {
    throw new Error("PowerPoint isn't available. Please reload the add-in and try again.");
  }

  return ppt().run(async (context) => {
    const shape = await findShapeOnSlide(context, selection.slideIndex, selection.shapeId);
    if (!shape) {
      throw new Error("The selected table could not be found — it may have been moved or deleted.");
    }

    const table = shape.getTable();
    table.load("values");
    await context.sync();

    return { type: "table", rows: table.values } as InsightsContent;
  });
}

type SlideImageCapable = PowerPoint.Slide & {
  getImageAsBase64?: (opts: { width: number; height: number }) => OfficeExtension.ClientResult<string>;
};

/** No shape selected — captures the current slide itself as a PNG, the same
 * way Share & Export renders slides for its PDF (see pdfExport.ts). */
function extractSlideContent(slideIndex: number): Promise<InsightsContent> {
  if (!hasPpt()) {
    throw new Error("PowerPoint isn't available. Please reload the add-in and try again.");
  }

  return ppt().run(async (context) => {
    const slide = context.presentation.slides.getItemAt(slideIndex - 1) as SlideImageCapable;
    if (typeof slide.getImageAsBase64 !== "function") {
      throw new TypeError(
        "Capturing the slide as an image needs PowerPointApi 1.5+ — try PowerPoint on the web, or update desktop PowerPoint.",
      );
    }
    const result = slide.getImageAsBase64({ width: SLIDE_WIDTH, height: SLIDE_HEIGHT });
    await context.sync();
    return { type: "image", base64: result.value, mimeType: "image/png" } as InsightsContent;
  });
}

/** Unzips the active (last-saved) presentation and parses the requested slide's XML.
 * Exported for selectionDetector.ts's ambiguous-placeholder probe, which needs the same
 * slide document to check for a matching chart/picture without re-deriving this path logic. */
export async function loadActiveSlideDocument(
  slideIndex: number,
): Promise<{ zip: Unzipped; slideDoc: Document; slidePath: string }> {
  const bytes = await getActivePresentationBytes();
  const zip = unzipSync(new Uint8Array(bytes));
  const slidePath = resolveSlidePath(zip, slideIndex);
  const slideDoc = new DOMParser().parseFromString(readSlideXml(zip, slidePath), "text/xml");
  return { zip, slideDoc, slidePath };
}

/* ── Public entry point ──────────────────────────────────────────────────── */

export async function extractSelectionContent(
  selection: InsightsSelectionContext,
): Promise<InsightsContent> {
  if (selection.type === "table") {
    return extractTableContent(selection);
  }
  if (selection.type === "slide") {
    return extractSlideContent(selection.slideIndex);
  }

  const { zip, slideDoc, slidePath } = await loadActiveSlideDocument(selection.slideIndex);

  if (selection.type === "image") {
    return extractImageContent(zip, slideDoc, slidePath, selection.shapeName);
  }
  return extractChartContent(zip, slideDoc, slidePath, selection.shapeName);
}
